import { ArrowRight, Boxes, BriefcaseBusiness, ClipboardCheck, FileText, Wrench } from 'lucide-react';
import { usePlatform } from '@forq/platform';
import { Badge, Card, CustomizableMetrics } from '@forq/ui';
import { dashboardFeatures } from './manifest';

type DashboardTaskTone = 'overdue' | 'today' | 'soon' | 'later' | 'none';

export interface DashboardTaskItem {
  readonly id: string;
  readonly title: string;
  readonly meta: string;
  readonly tone: DashboardTaskTone;
  readonly route: string;
}

interface DashboardPageProps {
  readonly enabledFeatureIds: readonly string[];
  /** Open FORQ Signal tasks for the active tenant — the same source as the topbar bell. */
  readonly tasks?: readonly DashboardTaskItem[];
  readonly onOpenTask?: (route: string) => void;
}

const featureLabel = new Map(dashboardFeatures.map((feature) => [feature.id, feature.name]));

export function DashboardPage({ enabledFeatureIds, onOpenTask, tasks = [] }: DashboardPageProps) {
  const platform = usePlatform();
  const enabled = new Set(enabledFeatureIds);

  return (
    <div className="dashboard-page">
      <header className="dashboard-hero">
        <div>
          <span className="forq-eyebrow">{platform.activeTenant.shortName} · Montag, 13. Juli</span>
          <h1>Guten Morgen, {platform.user.displayName}.</h1>
          <p>Das neue FORQ lädt Module, Funktionen und Oberflächen unabhängig voneinander.</p>
        </div>
        <Badge>Foundation v0.1</Badge>
      </header>

      {enabled.has('dashboard.kpis') && (
        <section className="dashboard-kpis" aria-label={featureLabel.get('dashboard.kpis')}>
          <CustomizableMetrics
            pageKey="dashboard-kpis"
            tiles={[
              { key: 'open-orders', node: <Kpi icon={<BriefcaseBusiness />} label="Offene Vorgänge" value="24" note="5 bereit zur Abrechnung" /> },
              { key: 'workshop', node: <Kpi icon={<Wrench />} label="Werkstatt" value="11" note="3 warten auf Teile" /> },
              { key: 'approvals', node: <Kpi icon={<FileText />} label="Freigaben" value="7" note="2 davon überfällig" /> },
              { key: 'tasks-today', node: <Kpi icon={<ClipboardCheck />} label="Aufgaben heute" value="18" note="8 bereits erledigt" /> },
            ]}
          />
        </section>
      )}

      <div className="dashboard-grid">
        <div className="dashboard-column">
          {enabled.has('dashboard.quick-actions') && (
            <Card eyebrow="Direkt starten" title="Schnellaktionen" action={<Badge>eigenes Feature</Badge>}>
              <div className="dashboard-actions">
                <QuickAction icon={<BriefcaseBusiness />} title="Neuen Auftrag" description="Service, intern, Verkauf oder Miete" />
                <QuickAction icon={<Wrench />} title="Maschine aufnehmen" description="Bestand oder Kundenmaschine" />
                <QuickAction icon={<FileText />} title="Rechnung prüfen" description="Offene Entwürfe und Freigaben" />
              </div>
            </Card>
          )}

          <Card eyebrow="Module Registry" title="Geplante Arbeitsbereiche">
            <div className="dashboard-module-roadmap">
              {['Customers', 'Machines', 'Orders', 'Service', 'Finance', 'Sales', 'Rental', 'Warehouse'].map((name, index) => (
                <div key={name}>
                  <span>{String(index + 1).padStart(2, '0')}</span>
                  <strong>{name}</strong>
                  <small>noch nicht registriert</small>
                </div>
              ))}
            </div>
          </Card>
        </div>

        <aside className="dashboard-column dashboard-column--aside">
          {enabled.has('dashboard.tasks') && (
            <Card eyebrow="Aufgabencenter" title="Was deine Aufmerksamkeit braucht" action={<Badge>{tasks.length} offen</Badge>}>
              {tasks.length === 0 ? (
                <p className="dashboard-tasks__empty">Keine offenen Aufgaben für {platform.activeTenant.shortName}.</p>
              ) : (
                <div className="dashboard-tasks">
                  {tasks.map((task) => (
                    <Task key={task.id} title={task.title} meta={task.meta} tone={task.tone} onOpen={() => onOpenTask?.(task.route)} />
                  ))}
                </div>
              )}
            </Card>
          )}

          {enabled.has('dashboard.architecture') && (
            <Card eyebrow="Architektur" title="Foundation-Status" action={<Boxes size={18} />}>
              <ul className="dashboard-checks">
                <li><span />Kernel ohne Fachwissen</li>
                <li><span />Module Registry aktiv</li>
                <li><span />Features einzeln schaltbar</li>
                <li><span />Shell und Theme austauschbar</li>
                <li><span />Mandantenkontext aktiv</li>
              </ul>
              <div className="dashboard-feature-toggles">
                <span className="forq-eyebrow">Feature Flags</span>
                {dashboardFeatures.map((feature) => (
                  <label key={feature.id}>
                    <input
                      type="checkbox"
                      checked={platform.isFeatureEnabled(feature.id)}
                      onChange={(event) => platform.setFeatureEnabled(feature.id, event.target.checked)}
                    />
                    <span>{feature.name}</span>
                  </label>
                ))}
              </div>
            </Card>
          )}
        </aside>
      </div>
    </div>
  );
}

function Kpi({ icon, label, note, value }: { readonly icon: React.ReactNode; readonly label: string; readonly note: string; readonly value: string }) {
  return (
    <article className="dashboard-kpi">
      <span className="dashboard-kpi__icon">{icon}</span>
      <div><small>{label}</small><strong>{value}</strong><p>{note}</p></div>
    </article>
  );
}

function QuickAction({ description, icon, title }: { readonly description: string; readonly icon: React.ReactNode; readonly title: string }) {
  return (
    <button type="button" className="dashboard-action">
      <span>{icon}</span><span><strong>{title}</strong><small>{description}</small></span><ArrowRight size={17} />
    </button>
  );
}

function Task({ meta, onOpen, title, tone }: { readonly meta: string; readonly onOpen?: () => void; readonly title: string; readonly tone?: DashboardTaskTone }) {
  return (
    <button type="button" className="dashboard-task" onClick={onOpen}>
      <span className={`dashboard-task__dot dashboard-task__dot--${tone ?? 'none'}`} /><span><strong>{title}</strong><small>{meta}</small></span><ArrowRight size={16} />
    </button>
  );
}
